import { jsx as _jsx } from "react/jsx-runtime";
import { Calendar } from "@air/next-icons";
import { Button } from "@air/primitive-button";
import { DateInput } from "@air/primitive-date-input";
import { tailwindMerge } from "@air/tailwind-variants";
import { parseAirDateToISO } from "@air/utilities";
import { reportErrorToBugsnag } from "@air/utils-error";
import { format } from "__barrel_optimize__?names=format!=!date-fns";
import { memo, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { usePrevious } from "__barrel_optimize__?names=usePrevious!=!react-use";
export const EditableDate = /*#__PURE__*/ memo((param)=>{
    let { ["data-testid"]: testId, isEditing = false, onEditingStateChange, placeholder, readOnly, name, dateValue = "", onChange, size = "large", buttonClassName, showCalendarIcon = true } = param;
    const [isEditingState, setIsEditingState] = useState(isEditing);
    const isPreviousIsEditing = usePrevious(isEditingState);
    const buttonRef = useRef(null);
    const dateInputRef = useRef(null);
    const setIsEditing = useCallback((isEditing)=>{
        setIsEditingState(isEditing);
        onEditingStateChange === null || onEditingStateChange === void 0 ? void 0 : onEditingStateChange(isEditing);
    }, [
        onEditingStateChange
    ]);
    const buttonText = useMemo(()=>{
        /** `dateValue` shows Invalid Date on Safari, so we reformat it here */ const formattedDate = dateValue ? parseAirDateToISO(dateValue).toString() : undefined;
        try {
            return formattedDate ? format(new Date(formattedDate), "MM/dd/yyyy") : placeholder;
        } catch (error) {
            reportErrorToBugsnag({
                error,
                context: "Failed to format date in EditableDate",
                metadata: {
                    dateValue,
                    formattedDate
                }
            });
            return "";
        }
    }, [
        dateValue,
        placeholder
    ]);
    useEffect(()=>{
        if (isEditingState && (dateInputRef === null || dateInputRef === void 0 ? void 0 : dateInputRef.current)) {
            var _dateInputRef_current;
            (_dateInputRef_current = dateInputRef.current) === null || _dateInputRef_current === void 0 ? void 0 : _dateInputRef_current.focus();
            dateInputRef.current.setSelectionRange(dateInputRef.current.value.length, dateInputRef.current.value.length);
        }
        if (!isEditingState && isPreviousIsEditing && (buttonRef === null || buttonRef === void 0 ? void 0 : buttonRef.current)) {
            buttonRef.current.focus();
        }
    }, [
        buttonRef,
        isEditingState,
        isPreviousIsEditing,
        dateInputRef
    ]);
    return /*#__PURE__*/ _jsx("div", {
        className: tailwindMerge("flex w-full rounded-4", readOnly ? "cursor-default" : "cursor-pointer"),
        "data-testid": testId,
        children: isEditingState ? /*#__PURE__*/ _jsx(DateInput, {
            name: name,
            testId: "DATE_INPUT_FIELD",
            showCalendarIcon: showCalendarIcon,
            onSelect: (_, e)=>{
                e === null || e === void 0 ? void 0 : e.stopPropagation();
            },
            className: "w-full",
            size: size,
            autoFocus: true,
            selected: dateValue ? parseAirDateToISO(dateValue) : undefined,
            placeholderText: placeholder,
            // timeouts are so clear button has a chance to trigger before close/blur do which unrender the component
            onCalendarClose: ()=>setTimeout(()=>setIsEditing(false), 100),
            onBlur: ()=>setTimeout(()=>setIsEditing(false), 100),
            onChange: (date)=>{
                if (Array.isArray(date)) return;
                setIsEditing(false);
                onChange(date);
            },
            ref: dateInputRef
        }) : /*#__PURE__*/ _jsx(Button, {
            appearance: "ghost",
            color: "grey",
            prefix: showCalendarIcon ? /*#__PURE__*/ _jsx(Calendar, {
                className: "size-4"
            }) : undefined,
            disabled: readOnly,
            onClick: ()=>{
                setIsEditing(true);
            },
            size: "large",
            className: tailwindMerge("w-full justify-start pl-2 font-normal disabled:bg-transparent", !dateValue ? "text-grey-7" : "", buttonClassName),
            ref: buttonRef,
            children: buttonText
        })
    });
});
EditableDate.displayName = "EditableDate";
